// BlogPost.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Divider, Typography } from '@mui/material';
import client from '../../utils/contentfulClient';
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'; // Optional: For custom renderers
import { useMobileContext } from '../../contexts/MobileContext';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import Image from '../../components/Image/Image';
import LoadingDots from '../../components/LoadingDots/LoadingDots';
import { headingLetterSpacing } from '../../utils/headingTypography';
import BlogCard from './BlogCard';
import { readReturnContext } from './blogReturnPage';
import Grid from '@mui/material/Grid';
import MobileComparisonAccordionTable from '../../components/HomePage/SeoDetailModule/MobileComparisonAccordionTable';

import FooterComponent from '../../components/HomePage/Footer/FooterComponent';
import FooterMobileComponent from '../../components/HomePage/Footer/FooterMobileComponent';
import SeoTagsManager from '../../utils/SeoTagsManager';
import { toTitleCase } from '../../utils/casing';

// ---- Cloudflare R2 icon URLs ----
const FACEBOOK_ICON = 'https://assets.askhippo.co.za/Pages/Footer/Facebook.svg';
const LINKEDIN_ICON = 'https://assets.askhippo.co.za/Pages/Footer/LinkedIn.svg';
const X_ICON = 'https://assets.askhippo.co.za/Pages/Footer/X.png';

const extractCellText = (cellNode) => {
  const walk = (node) => {
    if (!node) return '';
    if (node.nodeType === 'text') return node.value || '';
    if (Array.isArray(node.content)) return node.content.map(walk).join(' ');
    return '';
  };
  return walk(cellNode).trim();
};

const WORDS_PER_MINUTE = 225;

const estimateReadTime = (richTextDocument) => {
  const wordCount = extractCellText(richTextDocument)
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.round(wordCount / WORDS_PER_MINUTE));
};

const socialLinks = [
  {
    icon: FACEBOOK_ICON,
    url: 'https://www.facebook.com/HippoSA',
    alt: 'Facebook',
  },
  {
    icon: LINKEDIN_ICON,
    url: 'https://www.linkedin.com/company/hippo-comparative-services-pty-ltd',
    alt: 'LinkedIn',
  },
  {
    icon: X_ICON,
    url: 'https://twitter.com//hippo-co-za',
    alt: 'X / Twitter',
  },
];

const BlogPost = () => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextURL, setNextURL] = useState(null);
  const [prevURL, setPrevURL] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const isMobile = useMobileContext();
  const isDesktop = Boolean(isMobile.isDesktop);

  useEffect(() => {
    const pathname = window.location.pathname;
    const segments = pathname.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1] || '';
    // Slugs may be stored in Contentful with or without a trailing slash,
    // so match either form regardless of how the URL was requested.
    const slugCandidates = [...new Set([lastSegment, `${lastSegment}/`])];

    const fetchPost = async () => {
      try {
        setLoading(true);
        const entries = await client.getEntries({
          content_type: 'blogPost', // your content type
          'fields.slug[in]': slugCandidates,
        });

        if (entries.items.length > 0) {
          //   console.debug(entries.items[0]);
          getAdjacentPosts(entries.items[0]);
          getRelatedPosts(entries.items[0]);
          setPost(entries.items[0]);
          return entries.items[0];
        } else {
          return null;
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, []);

  const RELATED_POSTS_LIMIT = 4;

  const getRelatedPosts = async (currentPost) => {
    try {
      const response = await client.getEntries({
        content_type: 'blogPost',
        'fields.category': currentPost.fields.category,
        'sys.id[ne]': currentPost.sys.id,
        order: '-fields.blogDate',
        limit: RELATED_POSTS_LIMIT,
      });

      setRelatedPosts(response.items);
    } catch (err) {
      setRelatedPosts([]);
    }
  };

  const getAdjacentPosts = async (currentPost) => {
    try {
      const [prevRes, nextRes] = await Promise.all([
        // Previous post (older)
        client.getEntries({
          content_type: 'blogPost',
          'fields.blogDate[lt]': currentPost.fields.blogDate,
          order: '-fields.blogDate',
          limit: 1,
        }),
        // Next post (newer)
        client.getEntries({
          content_type: 'blogPost',
          'fields.blogDate[gt]': currentPost.fields.blogDate,
          order: 'fields.blogDate',
          limit: 1,
        }),
      ]);

      const prevPost = prevRes.items[0] || null;
      const nextPost = nextRes.items[0] || null;

      if (nextPost) {
        setNextURL(
          `/blog/${nextPost?.fields.category}/${nextPost?.fields.slug}`
        );
      } else {
        setNextURL(null);
      }

      if (prevPost) {
        setPrevURL(
          `/blog/${prevPost?.fields.category}/${prevPost?.fields.slug}`
        );
      } else {
        setPrevURL(null);
      }

      return { prevPost, nextPost };
    } catch (err) {
      return { prevPost: null, nextPost: null };
    }
  };

  if (loading)
    return (
      <>
        {/* Post title/description aren't known until the fetch resolves, but
            the URL alone (e.g. /blog/insurance/...) resolves to a sensible
            category title via SeoTagsManager, so the tab doesn't sit on the
            default homepage title while loading. */}
        {SeoTagsManager.render(window.location.pathname)}
        <div
          style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }}
        >
          <LoadingDots variant="otp" />
        </div>
      </>
    );
  if (error) return <div>Error: {error}</div>;
  if (!post) return <div>No post found</div>;

  const seoSchema = post?.fields?.seoSchema || null;
  const BASE_URL = 'https://www.hippo.co.za';

  const pageUrl =
    typeof window !== 'undefined'
      ? `${BASE_URL}${window.location.pathname}`
      : `${BASE_URL}/blog/${post.fields.category}/${post.fields.slug}/`;

  const ogImage = post?.fields?.headingImage?.fields?.file?.url
    ? `https:${post.fields.headingImage.fields.file.url}`
    : undefined;

  // Build BlogPosting node per spec Part 3 — publisher links to global org anchor
  const blogPostingNode = {
    '@type': 'BlogPosting',
    '@id': `${pageUrl}/#article`,
    headline: seoSchema?.name || post.fields.heading,
    description: seoSchema?.description || post.fields.blurb,
    ...(ogImage ? { image: ogImage } : {}),
    datePublished: post.sys.createdAt?.split('T')[0],
    dateModified: post.sys.updatedAt?.split('T')[0],
    author: seoSchema?.author || {
      '@type': 'Person',
      name: 'Hippo Staff Writer',
    },
    publisher: { '@id': `${BASE_URL}/#organization` },
    mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
  };

  const breadcrumbNode = {
    '@type': 'BreadcrumbList',
    '@id': `${pageUrl}/#breadcrumb`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: `${BASE_URL}/blog`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: toTitleCase(post.fields.category),
        item: `${BASE_URL}/blog/${post.fields.category}`,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: post.fields.heading,
        item: pageUrl,
      },
    ],
  };

  // Sends the reader back to the exact listing they clicked through from —
  // e.g. the "All" tab at page 3 — rather than always resetting to the
  // post's own category at page 1. "All" is treated as a real category
  // here, same as any named one. The URL is the literal one the listing
  // page itself navigated to (trailing slash, ?Year= filters and all),
  // read from sessionStorage rather than a query param so the post's
  // canonical URL stays clean. Falls back to the post's own category when
  // there's no stored context (direct link, search engine, shared URL, etc).
  const returnContext = readReturnContext();
  const returnCategoryLabel =
    returnContext?.subsection || toTitleCase(post.fields.category);
  const categoryHref =
    returnContext?.routedFromURL || `/blog/${post.fields.category}`;
  const authorName = post.fields.author || 'Hippo Staff Writer';
  const readTimeMinutes = estimateReadTime(post.fields.blogBody);

  const formatDate = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : null;

  // Published date is the editorial blogDate field (falls back to Contentful's
  // entry-creation timestamp if that's ever unset). Updated date is Contentful's
  // last-save timestamp — only shown when it's a different calendar day from
  // publication, so a post that's never been edited doesn't show two identical
  // dates.
  const publishedDate = formatDate(post.fields.blogDate || post.sys.createdAt);
  const updatedDate = formatDate(post.sys.updatedAt);
  const showUpdatedDate =
    updatedDate && publishedDate && updatedDate !== publishedDate;

  const seoOverrides = {
    title: seoSchema?.name || post.fields.heading,
    description: seoSchema?.description || post.fields.blurb,
    // Comma-delimited Contentful field; blank falls back to the default list.
    seoKeywords: post.fields.seoKeywords,
    canonical: pageUrl,
    extra: {
      ogImage,
      ldNodes: [blogPostingNode, breadcrumbNode],
      // BlogPosting.mainEntityOfPage covers the WebPage relationship
      webPageNode: false,
    },
  };

  const renderOptions = {
    renderMark: {
      [MARKS.BOLD]: (text) => (
        <strong
          style={{ fontWeight: 'bold', color: 'rgba(22, 47, 59, 1)' }}
          className="font-bold"
        >
          {text}
        </strong>
      ),
      [MARKS.ITALIC]: (text) => <em className="italic">{text}</em>, // Custom italic
    },
    renderNode: {
      [BLOCKS.HEADING_1]: (node, children) => (
        <h1
          className="text-4xl font-bold mb-4"
          style={{ letterSpacing: headingLetterSpacing('2.25rem') }}
        >
          {children}
        </h1>
      ),
      // Table rendering — desktop keeps a real <table>; mobile uses the
      // same accordion-card table used by SeoDetailMedicalAid.
      [BLOCKS.TABLE]: (node, children) => {
        if (isDesktop) {
          return (
            <table
              style={{
                width: '100%',
                border: '1px solid #d1d5db', // Grey outline
                borderCollapse: 'collapse',
              }}
            >
              <tbody>{children}</tbody>
            </table>
          );
        }

        // Treat the first row of any multi-row table as the label row so
        // its text labels every other row's dropdown values instead of
        // rendering as its own standalone accordion item — most authors
        // don't toggle Contentful's explicit "header row" cell formatting,
        // so we can't rely on cell nodeType to detect it.
        const rowNodes = node.content || [];
        const hasHeaderRow = rowNodes.length > 1;
        const headerRowNode = hasHeaderRow ? rowNodes[0] : null;
        const dataRowNodes = hasHeaderRow ? rowNodes.slice(1) : rowNodes;

        const rows = dataRowNodes.map((rowNode) =>
          (rowNode.content || []).map(extractCellText)
        );

        // MobileComparisonAccordionTable only renders a row's expandable
        // detail content when headers.length > 1, so synthesize blank
        // headers sized to the widest row when there's no header row at
        // all, otherwise multi-column rows would silently fail to expand.
        const columnCounts = rows.map((row) => row.length);
        if (headerRowNode)
          columnCounts.push((headerRowNode.content || []).length);
        const columnCount = columnCounts.length ? Math.max(...columnCounts) : 0;
        const headers = headerRowNode
          ? headerRowNode.content.map(extractCellText)
          : new Array(columnCount).fill('');

        return (
          <MobileComparisonAccordionTable
            table={{ headers, rows }}
            showHeaderTitle={false}
            defaultOpenIndex={null}
          />
        );
      },
      [BLOCKS.TABLE_ROW]: (node, children) => <tr>{children}</tr>,
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const file = node.data.target.fields.file;
        const dims = file.details?.image;
        return (
          <img
            src={file.url}
            alt={node.data.target.fields.title}
            width={dims?.width}
            height={dims?.height}
            loading="lazy"
            decoding="async"
            style={{
              display: 'block',
              // Unlike the curated card-thumbnail image (a single field with
              // a consistent 3:2 spec), these are ad-hoc images editors paste
              // into the body — actual ratios vary post to post (measured
              // anywhere from ~1.5 to ~1.9). A fixed aspect-ratio box would
              // crop whichever images don't match it, and a maxHeight cap
              // with contain would leave empty letterbox space instead of
              // filling the column. width:100%/height:auto is the one
              // setting that always fills the full column width AND shows
              // 100% of the image, whatever its native shape is.
              width: '100%',
              height: 'auto',
              borderRadius: '15px',
              marginBottom: '1.5rem',
            }}
          />
        );
      },
      [BLOCKS.TABLE_HEADER_CELL]: (node, children) => (
        <th
          style={{
            border: '1px solid #d1d5db', // Grey outline
            backgroundColor: 'rgba(199, 203, 0, 1)', // Green background
            color: 'white', // White text for contrast
            padding: '8px 16px', // Padding for spacing
            textAlign: 'left', // Default alignment
          }}
        >
          {children}
        </th>
      ),
      [BLOCKS.TABLE_CELL]: (node, children) => (
        <td
          style={{
            border: '1px solid #d1d5db', // Grey outline
            padding: '8px 16px', // Padding for spacing
          }}
        >
          {children}
        </td>
      ),
      [INLINES.HYPERLINK]: (node, children) => (
        <a
          href={node.data.uri}
          style={{
            color: 'rgba(199, 203, 0, 1)', // Specified yellowish-green color
            textDecoration: 'none',
            fontWeight: 'bold',
          }}
        >
          {children}
        </a>
      ),
    },
  };

  return (
    <div>
      {SeoTagsManager.render(window.location.pathname, seoOverrides)}

      <Box
        sx={{
          maxWidth: 1600,
          width: '100%',
          margin: '0 auto',
          px: { xs: 2, sm: 3, md: 4 },
          pt: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              maxWidth: 780,
              width: '100%',
              mx: 'auto',
              mt: { xs: 0, md: 1 },
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography
                component={Link}
                to={categoryHref}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  lineHeight: 1,
                  letterSpacing: 0,
                  color: '#1E354E',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                &larr; Return to {returnCategoryLabel}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '0.75rem',
                lineHeight: '1.25rem',
                letterSpacing: '1%',
                textAlign: { xs: 'left', md: 'right' },
                mb: '24px',
                color: '#738289',
              }}
            >
              {authorName}
              {publishedDate && ` · Published ${publishedDate}`}
              {showUpdatedDate && ` · Updated ${updatedDate}`}
              {` · ${readTimeMinutes} min read`}
            </Typography>
            <h1
              style={{
                letterSpacing: headingLetterSpacing('2.5rem'),
                textWrap: 'pretty',
              }}
            >
              {post.fields.heading}
            </h1>
            <div>
              {documentToReactComponents(post.fields.blogBody, renderOptions)}
            </div>
            <div style={{ marginTop: 60 }}></div>

            {relatedPosts.length > 0 && (
              <>
                <Divider
                  sx={{
                    mb: 4,
                    bgcolor: 'rgba(227, 230, 232, 1)',
                    height: '1px',
                  }}
                />
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                  Related Articles
                </Typography>
                <Grid container spacing={2} sx={{ mb: 6 }}>
                  {relatedPosts.map((relatedPost) => (
                    <Grid
                      item
                      key={relatedPost.sys.id}
                      xs={12}
                      sm={6}
                      sx={{ display: 'flex', justifyContent: 'center' }}
                    >
                      <BlogCard
                        title={relatedPost.fields.heading}
                        image={relatedPost.fields.headingImage.fields.file.url}
                        date={relatedPost.fields.blogDate}
                        blurb={relatedPost.fields.blurb}
                        slug={relatedPost.fields.slug}
                        category={relatedPost.fields.category}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            <Divider
              sx={{
                mb: 2,
                bgcolor: 'rgba(227, 230, 232, 1)',
                height: '1px',
              }}
            />
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: '100px',
              }}
            >
              <div>
                <a
                  href={prevURL}
                  style={{
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    pointerEvents: prevURL ? 'auto' : 'none',
                  }}
                >
                  <Typography
                    style={{
                      color: prevURL ? null : '#d2d2d2',
                    }}
                  >
                    Previous Post
                  </Typography>
                </a>
              </div>
              <div
                style={{ display: 'flex', gap: '10px', flexDirection: 'row' }}
              >
                {socialLinks.map(({ icon, url, alt }, i) => (
                  <Box
                    key={i}
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderRadius: '50%',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Image
                      src={icon}
                      alt={alt}
                      width={30}
                      height={30}
                      style={{ width: '30px', height: '30px' }}
                    />
                  </Box>
                ))}
              </div>
              <div>
                <a
                  href={nextURL}
                  style={{
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    pointerEvents: nextURL ? 'auto' : 'none',
                  }}
                >
                  <Typography
                    style={{
                      color: nextURL ? null : '#d2d2d2',
                    }}
                  >
                    Next Post
                  </Typography>
                </a>
              </div>
            </div>
          </Box>
        </Box>
      </Box>

      {isDesktop ? <FooterComponent /> : <FooterMobileComponent />}
    </div>
  );
};

export default BlogPost;

// Space ID:

// w8xk6b5tz811

// Content Delivery API - access token

// 8s72oAHFJmV-acm8Gyxy2F-atrZ5Pp8NnpZiN5MuwQA

// Content Preview API - access token

// UdMwpsBNXbfCfH8YX3l4X7dQ980lzRVD_O72deiPgZs
